import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { client } from '../api';
import { setAnalyticsIdentity } from 'shared-utils/analytics';

interface FormData {
  token: string;
  isImpersonated?: boolean;
}

interface LoginResponse {
  member_id: string;
  email: string;
  practice_key: string;
  impersonator_member_id?: string;
  impersonator_email?: string;
}

interface ErrorResponse {
  error?: string;
}

type LoginMutationConfig = {
  onSuccess?: (response: LoginResponse) => void | Promise<void>;
  onError?: (error: AxiosError<ErrorResponse>) => void;
};

/**
 * Set FullStory identity for the current user.
 * https://developer.fullstory.com/browser/identification/identify-users/
 */
const setFSIdentity = (response: LoginResponse) => {
  if (response.impersonator_member_id) {
    setAnalyticsIdentity(
      response.impersonator_member_id,
      response.impersonator_email!,
      `${response.impersonator_email} as ${response.email}`,
      {
        practice_key: response.practice_key,
      },
    );
  } else {
    setAnalyticsIdentity(response.member_id, response.email, response.email, {
      practice_key: response.practice_key,
    });
  }
};

export const useLoginMutation = (config: LoginMutationConfig = {}) => {
  const queryClient = useQueryClient();

  const login = useMutation<LoginResponse, AxiosError<ErrorResponse>, FormData>({
    mutationFn: async (data) => {
      const endpoint = data.isImpersonated ? 'login-impersonated' : 'login';
      const res = await client.post<LoginResponse>(endpoint, { token: data.token });
      return res.data;
    },
    onSuccess: async (response) => {
      setFSIdentity(response);
      await queryClient.invalidateQueries({ queryKey: ['checkLogin'] });

      if (config.onSuccess) {
        await config.onSuccess(response);
      }
    },
    onError: (error) => {
      if (config.onError) {
        config.onError(error);
      }
    },
  });

  return login;
};
